import { Button, Split, SplitItem } from '@patternfly/react-core';
import { AngleLeftIcon, AngleRightIcon } from '@patternfly/react-icons';
import * as React from 'react';

import { HISTORY_PAGE_SIZE } from '@app/utils/config';

export interface HistoryPagerProps {
  /** Zero based, as the endpoint counts. */
  page: number;
  /** Revisions on the page in hand, for the range readout. */
  count: number;
  /** Straight from the response. The only thing that says whether there is another page. */
  hasMore: boolean;
  isLoading: boolean;
  onPageChange(page: number): void;
  /** What is being paged, for the readout: "revisions", "changes". */
  itemsLabel?: string;
}

/**
 * Newer / older, rather than a PatternFly Pagination.
 *
 * There is no total to page against: the history endpoints answer `hasMore` and never a count, on
 * purpose - the service reads one revision past the page to diff the oldest one on it, and that
 * extra read *is* `hasMore`. A Pagination needs an itemCount, and the only one available here is a
 * lower bound, which would make its "last page" button lie.
 *
 * Renders nothing at all when there is one page and no more, so a short trail carries no chrome.
 */
export const HistoryPager: React.FunctionComponent<HistoryPagerProps> = ({
  page,
  count,
  hasMore,
  isLoading,
  onPageChange,
  itemsLabel = 'revisions'
}) => {
  if (page === 0 && !hasMore) return null;

  const first = page * HISTORY_PAGE_SIZE + 1;

  return (
    <Split hasGutter style={{ padding: 'var(--pf-t--global--spacer--md) 0', alignItems: 'center' }}>
      <SplitItem>
        <Button
          variant="link"
          icon={<AngleLeftIcon />}
          isDisabled={page === 0 || isLoading}
          onClick={() => onPageChange(page - 1)}
        >
          Newer
        </Button>
      </SplitItem>
      <SplitItem>
        <span style={{ color: 'var(--pf-t--global--text--color--subtle)' }}>
          {count > 0 ? `${itemsLabel} ${first}–${first + count - 1}` : `no ${itemsLabel} on this page`}
        </span>
      </SplitItem>
      <SplitItem>
        <Button
          variant="link"
          icon={<AngleRightIcon />}
          iconPosition="end"
          isDisabled={!hasMore || isLoading}
          onClick={() => onPageChange(page + 1)}
        >
          Older
        </Button>
      </SplitItem>
    </Split>
  );
};
